import { createAsyncThunk } from '@reduxjs/toolkit';
import { ProspectDataDto } from '@zaplify/prospects';
import { ProfilesSdk } from '@zaplify/sdk';
import { batch } from 'react-redux-latest';
import { NavigateFunction } from 'react-router-dom';
import { DEFAULT_COMPANIES_PER_PAGE } from '../../../config';
import { handleErrorNotification, handleMoveProspects, handleSuccessNotification } from '../../actions';
import { ProspectSearchFilterState } from '../prospect-search-filters/types';
import { getFormattedProspectSearch, getSelectedProspectsArray } from './helpers';
import {
    addHubspotRequestedProspects,
    addPipedriveRequestedProspects,
    addSalesforceRequestedProspects,
    addUpsalesRequestedProspects,
    addWebhookRequestedProspects,
    resetHasMoreProspects,
    updateAllSelected,
    updateBulkProspectTransactionState,
    updateDetailedProspects,
    updateErrors,
    updateLoadingHitsState,
    updateProspectHits,
    updateProspectsCount,
    updatePurchasingProspectsState,
    updatePurchasingToOtherCampaign,
    updateSelected,
} from './slice';
import { PreviewProspectsDto, ProspectHits, ProspectSearchResultStateType, ProspectsTransactionState } from './types';
import { SearchScopes } from '@zaplify/constants';
import { setLocalStorage } from '../../../services/utils/localStorage';
import { clearLocalStorageByKey } from '../../../services/utils/localStorage';

export enum TransactionState {
    BOUGHT = 'bought',
    AVAILABLE = 'available',
}

type Props = {
    prospectSearchFilters: ProspectSearchFilterState;
    pagination?: boolean;
    navigate: NavigateFunction;
    companiesPerPage?: number;
};

export const previewProspectsAction = createAsyncThunk(
    'preview/prospects',
    // setting thunkAPI to type "AsyncThunkConfig" is causing multiple typing errors
    async (
        { prospectSearchFilters, pagination, navigate, companiesPerPage = DEFAULT_COMPANIES_PER_PAGE }: Props,
        thunkAPI: any
    ): Promise<PreviewProspectsDto> => {
        const {
            getState,
            extra: { zaplifySdk },
            dispatch,
        } = thunkAPI;
        const profilesSdk = zaplifySdk.profiles as ProfilesSdk;
        const feedView = getState().prospectSearchFilters.searchScope === SearchScopes.feed;

        batch(() => {
            dispatch(updateErrors({ type: 'previewProspectsAction', value: false }));
            dispatch(resetHasMoreProspects());
            if (!pagination) {
                dispatch(updateProspectsCount(0));
                dispatch(updateLoadingHitsState(true));
                dispatch(updateDetailedProspects({}));
            }
        });

        try {
            const prospectSearchResultState = getState().prospectSearchResult;
            const page = prospectSearchResultState.page;

            if (!pagination) dispatch(updateProspectHits({} as ProspectHits));

            const fromPage = (page - 1) * companiesPerPage;
            const prospectSearchFilter = getFormattedProspectSearch({
                filters: prospectSearchFilters,
                fromCompanyIndex: fromPage,
                amountOfCompanies: companiesPerPage,
            });
            const prospectsResponse = await profilesSdk.sources.previewProspectingList(
                prospectSearchFilter,
                feedView,
                'campaignIDTEST____TEST'
            );

            //Calculating total number of prospects in result
            const numProspectsInResult = prospectsResponse?.persons?.length;

            const counts = prospectsResponse.counts;

            const hits = {
                total: counts.totalPersons.value,
                available: counts.availablePersons.value,
                blocklisted: counts.blocklistedPersons.value,
                assigned: counts.assignedPersons.value,
                availableCompanies: counts.availableCompanies.value,
            };

            batch(() => {
                if (!pagination) dispatch(updateSelected({}));
                dispatch(updateProspectsCount(numProspectsInResult || 0));
                dispatch(updateProspectHits(hits));
            });

            const aggregations = prospectsResponse.aggregations;

            //Adding params to url for intercom
            const params = new URLSearchParams(window.location.search);
            params.set('prospects-loaded', 'true');
            params.set('hits-loaded', 'true');
            navigate(`${window.location.pathname}?${params.toString()}`);

            const userId = getState().user.zaplifyUser.id;
            if (!userId)
                return {
                    persons: prospectsResponse.persons,
                    aggregations: aggregations,
                };
            if (prospectsResponse.persons.length > 0) {
                clearLocalStorageByKey(`prospectSearchFilters-userId:${userId}`);
                setLocalStorage(`prospectSearchFilters-userId:${userId}`, JSON.stringify(prospectSearchFilters));
            }

            return {
                persons: prospectsResponse.persons,
                aggregations: aggregations,
            };
        } catch (error) {
            console.log('previewProspectsAction error', error);
            dispatch(handleErrorNotification("Couldn't load prospects.", 2));
            dispatch(updateErrors({ type: 'previewProspectsAction', value: true }));
        } finally {
            dispatch(updateLoadingHitsState(false));
        }
    }
);

type getFeedProps = {
    includeCampaign?: boolean;
};

export const getFeed = createAsyncThunk(
    'loading-feed',
    // setting thunkAPI to type "AsyncThunkConfig" is causing multiple typing errors
    async ({ includeCampaign = true }: getFeedProps, thunkAPI: any): Promise<ProspectDataDto[]> => {
        const {
            getState,
            extra: { zaplifySdk },
            dispatch,
        } = thunkAPI;
        const profilesSdk = zaplifySdk.profiles as ProfilesSdk;

        try {
            const prospectSearchFilters = getState().prospectSearchFilters;
            const feedView = getState().prospectSearchFilters.searchScope === SearchScopes.feed;
            const campaignId = includeCampaign ? getState().newCampaign.campaignId : undefined;

            const fromPage = 0;
            const prospectSearchFilter = getFormattedProspectSearch({
                filters: prospectSearchFilters,
                fromCompanyIndex: fromPage,
            });

            const prospectsResponse = await profilesSdk.sources.previewProspectingList(
                prospectSearchFilter,
                feedView,
                feedView ? campaignId : undefined
            );

            //Calculating total number of prospects in result
            const numProspectsInResult = prospectsResponse?.persons;

            const counts = prospectsResponse.counts;

            const hits = {
                total: counts.totalPersons.value,
                available: counts.availablePersons.value,
                blocklisted: counts.blocklistedPersons.value,
                assigned: counts.assignedPersons.value,
                availableCompanies: counts.availableCompanies.value,
            };

            batch(() => {
                dispatch(updateProspectHits(hits));
            });

            return prospectsResponse.persons;
        } catch (error) {
            console.log('Load More Prospects In Feed Error', error);
        } finally {
        }
    }
);

export const getRequestedProspectsConfiguration = (
    prospectSearchState: ProspectSearchResultStateType,
    inset: boolean,
    integrations: string[],
    fullPageSync: boolean
): {
    requestedProspects: string[];
    dispatchFn: ((prospectId: string[]) => {})[];
} => {
    const configuration = {
        HUBSPOT: {
            requestedProspects: prospectSearchState.hubspotRequestedProspects,
            dispatchFn: addHubspotRequestedProspects,
        },
        PIPEDRIVE: {
            requestedProspects: prospectSearchState.pipedriveRequestedProspects,
            dispatchFn: addPipedriveRequestedProspects,
        },
        SALESFORCE: {
            requestedProspects: prospectSearchState.salesforceRequestedProspects,
            dispatchFn: addSalesforceRequestedProspects,
        },
        UPSALES: {
            requestedProspects: prospectSearchState.upsalesRequestedProspects,
            dispatchFn: addUpsalesRequestedProspects,
        },
        WEBHOOK: {
            requestedProspects: prospectSearchState.webhookRequestedProspects,
            dispatchFn: addWebhookRequestedProspects,
        },
    };
    const requestedProspects: string[] = [];
    const dispatchFn: ((prospectId: string[]) => {})[] = [];
    if (inset) {
        for (const [key, value] of Object.entries(configuration)) {
            if (integrations.includes(key)) {
                requestedProspects.push(...Object.keys(value.requestedProspects));
                dispatchFn.push(value.dispatchFn);
            }
        }
    } else {
        if (!integrations.length || !fullPageSync) {
            for (const [key, value] of Object.entries(configuration)) {
                requestedProspects.push(...Object.keys(value.requestedProspects));
                dispatchFn.push(value.dispatchFn);
            }
        } else {
            for (const [key, value] of Object.entries(configuration)) {
                if (!integrations.includes(key)) {
                    requestedProspects.push(...Object.keys(value.requestedProspects));
                    dispatchFn.push(value.dispatchFn);
                }
            }
        }
    }

    return {
        requestedProspects,
        dispatchFn,
    };
};
export const bulkSyncProspects =
    (isFullPageSyncActivated = false) =>
    async (dispatch, getState) => {
        const prospectSearchState: ProspectSearchResultStateType = getState().prospectSearchResult;
        const results = getRequestedProspectsConfiguration(
            prospectSearchState,
            true,
            ['WEBHOOK'],
            isFullPageSyncActivated
        );

        try {
            const syncedProspectIds = results.requestedProspects;

            const syncingProspectIds = Object.keys(prospectSearchState.syncingProspects).filter(
                (prospect) => !syncedProspectIds.includes(prospect)
            );
            if (syncingProspectIds?.length > 0) {
                results.dispatchFn.forEach((dispatchFn) => {
                    dispatch(dispatchFn(syncingProspectIds));
                });
            }
        } catch (error) {
            dispatch(handleErrorNotification('Failed to load webhooks data from server', 2));
            console.log('Failed to load webhooks data from server');
        }
    };

export const fullBulkSyncProspectsPerIntegration =
    (integrationTypes: Array<string>) =>
    async (dispatch, getState, { getFirebase }) => {
        const prospectSearchState: ProspectSearchResultStateType = getState().prospectSearchResult;
        const results = getRequestedProspectsConfiguration(prospectSearchState, true, integrationTypes, true);

        const allPersons: string[] = [];
        getState().prospectSearchResult.prospectsInCompanies.forEach((prospectsInCampaign) => {
            const prospects = prospectsInCampaign.prospects;
            prospects.forEach((prospect) => {
                if (prospect.personId) {
                    allPersons.push(prospect.personId);
                }
            });
        });

        try {
            const syncedProspectIds = [...results.requestedProspects];

            const syncingProspectIds = allPersons.filter((prospect) => !syncedProspectIds.includes(prospect));

            if (syncingProspectIds?.length > 0) {
                results.dispatchFn.forEach((dispatchFn) => {
                    dispatch(dispatchFn(syncingProspectIds));
                });
            }
        } catch (error) {
            dispatch(handleErrorNotification('Failed to load webhooks data from server', 2));
            console.log('Failed to load webhooks data from server');
        }
    };

type PurchaseProspectsProps = {
    allProspectsAdded?: boolean;
    ids?: readonly string[];
    toOtherCampaignId?: string;
    toOtherGroupId?: string;
    navigate: NavigateFunction;
    resyncHits?: boolean;
};

export const purchaseProspects =
    ({
        ids,
        allProspectsAdded = false,
        toOtherCampaignId,
        toOtherGroupId,
        navigate,
        resyncHits = true,
    }: PurchaseProspectsProps) =>
    async (dispatch, getState, { getFirebase, zaplifySdk }) => {
        const profilesSdk: ProfilesSdk = zaplifySdk.profiles;
        try {
            dispatch(updatePurchasingProspectsState(true));
            toOtherCampaignId && dispatch(updatePurchasingToOtherCampaign(true));
            const selected = getState().prospectSearchResult.selected;
            const hits = getState().prospectSearchResult.hits;
            const purchased = getState().prospectSearchResult.prospectTransactionState;
            const campaignId = toOtherCampaignId || getState().newCampaign.campaignId;
            const groupId = toOtherGroupId;
            const numSelected = Object.keys(selected)?.filter((id) => selected[id])?.length;
            let selectedPersonIds: string[] =
                (ids?.length && [...ids]) || getSelectedProspectsArray(selected, purchased);
            const prospectTransactionState: ProspectsTransactionState = {};

            Object.keys(selectedPersonIds).forEach((id) => {
                prospectTransactionState[selectedPersonIds[id]] = TransactionState.BOUGHT;
            });

            const prospectsIdsToMarkAsBought =
                ids && ids.length > 0 ? prospectTransactionState : { [selectedPersonIds[0]]: TransactionState.BOUGHT };

            dispatch(updateBulkProspectTransactionState(prospectsIdsToMarkAsBought));

            const prospectIds = await profilesSdk.sources.purchaseToCampaign(
                { personIds: selectedPersonIds, linkedinUrls: [] },
                campaignId,
                groupId
            );

            batch(() => {
                {
                    resyncHits && dispatch(getProspectHits(navigate));
                }
                !toOtherCampaignId &&
                    dispatch(
                        handleSuccessNotification(`Successfully unlocked ${selectedPersonIds.length} prospects`, 2)
                    );
                toOtherCampaignId &&
                    dispatch(
                        handleSuccessNotification(
                            `${selectedPersonIds.length} ${
                                selectedPersonIds.length > 1 ? 'Prospects' : 'Prospect'
                            } added to another campaign`,
                            2
                        )
                    );
            });

            // const prospectDetails = await profilesSdk.prospects.getProspectByIdsBatch(prospectIds);
            // const formattedProspects = prospectDetails.reduce(
            //     (acc, curr) => ({ ...acc, [curr?.data?.personId]: curr.data }),
            //     {}
            // );
            // dispatch(updateDetailedProspects(formattedProspects));

            if (numSelected === hits) {
                dispatch(updateAllSelected(true));
            }
            if (numSelected > 0) {
                const tempSelected = { ...selected };
                selectedPersonIds?.forEach((id) => delete tempSelected[id]);
                dispatch(updateSelected(tempSelected));
            }

            dispatch(handleMoveProspects(false));
            return;
        } catch (error) {
            console.log('Failed to unlock', error);
            dispatch(handleErrorNotification('Failed to unlock prospects', 2));
        } finally {
            dispatch(updatePurchasingProspectsState(false));
            toOtherCampaignId && dispatch(updatePurchasingToOtherCampaign(false));
        }
    };

export const getProspectHits =
    (navigate: NavigateFunction) =>
    async (dispatch, getState, { getFirebase, zaplifySdk }) => {
        const profilesSdk: ProfilesSdk = zaplifySdk.profiles;
        const prospectSearchFilters = getState().prospectSearchFilters;

        try {
            dispatch(updateLoadingHitsState(true));

            const prospectSearchFilter = getFormattedProspectSearch({ filters: prospectSearchFilters });
            const prospectsResponse = await profilesSdk.sources.previewProspectingList(prospectSearchFilter, false);
            const counts = prospectsResponse.counts;
            const hits = {
                total: counts.totalPersons.value,
                available: counts.availablePersons.value,
                blocklisted: counts.blocklistedPersons.value,
                assigned: counts.assignedPersons.value,
                availableCompanies: counts.availableCompanies.value,
            };

            dispatch(updateProspectHits(hits));

            //Adding params to url for intercom
            const params = new URLSearchParams(window.location.search);
            params.set('hits-loaded', 'true');
            navigate(`${window.location.pathname}?${params.toString()}`);
        } catch (error) {
            console.log('getProspecthits error');
            dispatch(handleErrorNotification('Failed to prospects count', 2));
        } finally {
            dispatch(updateLoadingHitsState(false));
        }
    };

export const selectExtendedProspects = createAsyncThunk(
    'select-extended-prospects',
    async ({ totalNumberOfProspectsToGet }: { totalNumberOfProspectsToGet: number }, thunkAPI: any) => {
        const {
            dispatch,
            getState,
            extra: { zaplifySdk },
        } = thunkAPI;
        const profilesSdk: ProfilesSdk = zaplifySdk.profiles;
        const prospectSearchState = getState().prospectSearchFilters;
        const prospectSearchResultState = getState().prospectSearchResult;
        const purchasedProspects = prospectSearchResultState.prospectTransactionState;
        const currentPage = prospectSearchResultState.page;
        try {
            const numberOfProspectsOnCurrentPage = (prospectSearchResultState.prospectsInCompanies || []).reduce(
                (acc, curr) => (acc += curr.prospects.length),
                0
            );

            let availableProspectsOnPageCount = 0;

            const prospectsToSelectOnCurrentPage: { [key: string]: boolean } =
                prospectSearchResultState.prospectsInCompanies.reduce(
                    (acc, curr) => ({
                        ...acc,
                        ...curr.prospects.reduce((acc, curr: any) => {
                            if (purchasedProspects[curr.personId]) return acc;
                            //Just adding so many prospects that the user wants to
                            availableProspectsOnPageCount++;
                            if (availableProspectsOnPageCount > totalNumberOfProspectsToGet) return acc;

                            return { ...acc, [curr.personId]: true };
                        }, {}),
                    }),
                    {}
                );

            const extraProspectsLeft =
                totalNumberOfProspectsToGet - availableProspectsOnPageCount <= 0
                    ? 0
                    : totalNumberOfProspectsToGet - availableProspectsOnPageCount;
            const extraProspectsToSelect: { [key: string]: boolean } = Array.from(Array(extraProspectsLeft)).reduce(
                (acc, curr, idx) => ((acc[idx] = true), acc),
                {}
            );

            dispatch(updateSelected({ ...prospectsToSelectOnCurrentPage, ...extraProspectsToSelect }));

            if (totalNumberOfProspectsToGet <= availableProspectsOnPageCount) {
                return prospectsToSelectOnCurrentPage;
            }

            const numberOfProspectsToGetFromNextPages = totalNumberOfProspectsToGet - availableProspectsOnPageCount;
            const amountOfPagesToGet = Math.ceil(numberOfProspectsToGetFromNextPages / DEFAULT_COMPANIES_PER_PAGE);
            const prospectSearchFilter = getFormattedProspectSearch({
                filters: prospectSearchState,
                fromCompanyIndex: currentPage * DEFAULT_COMPANIES_PER_PAGE,
                amountOfCompanies: amountOfPagesToGet * DEFAULT_COMPANIES_PER_PAGE,
            });
            const prospectsResponse = await profilesSdk.sources.previewProspectingList(prospectSearchFilter, false);
            let prospectsIdsToAddCount = numberOfProspectsToGetFromNextPages;
            const prospectsIdsToAdd = prospectsResponse.persons.reduce((acc, curr, idx) => {
                if (purchasedProspects[curr.personId]) {
                    return acc;
                }
                --prospectsIdsToAddCount;
                if (prospectsIdsToAddCount < 0) {
                    return acc;
                }

                return { ...acc, [curr.personId]: true };
            }, {});

            const allIdsToSelect = { ...prospectsToSelectOnCurrentPage, ...prospectsIdsToAdd };

            //Checking if prospects are still selected, otherwise user might have clicked clear all
            if (Object.keys(getState().prospectSearchResult.selected).length > 0) {
                dispatch(updateSelected(allIdsToSelect));
                return allIdsToSelect;
            }
            return {};
        } catch (error) {
            dispatch(handleErrorNotification("Couldn't select prospects", 2));
            dispatch(updateSelected({}));
        }
    }
);
